import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { LiveBadge } from "./LiveBadge";
import type { FollowingStore } from "@/types/layout";

interface FollowingListItemProps {
  store: FollowingStore;
  isLast?: boolean;
  onUnfollow?: (sellerId: string) => void;
  onStoreClick?: (sellerId: string) => void;
  isUnfollowing?: boolean;
}

export function FollowingListItem({
  store,
  isLast,
  onUnfollow,
  onStoreClick,
  isUnfollowing = false,
}: FollowingListItemProps) {

   const handleStoreClick = () => {
    if (onStoreClick) {
      onStoreClick(store.id);
    }
  };

  const handleUnfollowClick = (e) => {
    e.stopPropagation();
    if (onUnfollow && !isUnfollowing) {
      onUnfollow(store.id);
    }
  };

  // Format item count (e.g. 9000 -> "9k", 1500 -> "1.5k")
  const formatItemCount = (count?: number): string => {
    if (count === undefined || count === null) return "0 Items Listed";

    if (count >= 1000) {
      const formatted = (count / 1000).toFixed(count % 1000 === 0 ? 0 : 1);
      return `${formatted}k Items Listed`;
    }
    return `${count} Items Listed`;
  };

  return (
    <div
      className={`flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 ${
        !isLast ? "border-b border-[rgba(255,255,255,0.05)]" : ""
      }`}
    >
      <div
        onClick={handleStoreClick}
        className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer
                  rounded-md hover:bg-sidebar-active-bg/5 active:bg-gray-200 transition-colors px-2 py-1"
      >
        <Avatar className="size-9 flex-shrink-0">
          <AvatarImage src={store.avatar} alt={store.name} />
          <AvatarFallback className="bg-gray-200 text-gray-600 text-xs">
            {store.name.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>

        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <h3 className="font-lexend text-[15px] sm:text-[17px] font-medium leading-none tracking-[-0.22px] capitalize text-black truncate">
            {store.name}
          </h3>

          <div className="flex items-center gap-[4.94px] flex-wrap">
            {store.isLive && <LiveBadge />}
            <span className="font-lexend text-[11px] sm:text-[12px] font-light leading-[14.40px] tracking-[-0.27px] capitalize text-black whitespace-nowrap">
              {formatItemCount(store.itemsCount)}
            </span>
          </div>
        </div>
      </div>

      <Button
        variant="ghost"
        onClick={handleUnfollowClick}
        disabled={isUnfollowing}
        className="font-lexend text-[12px] sm:text-[14px] font-normal tracking-[-0.22px] capitalize text-black bg-search-bg h-auto py-1.5 sm:py-2 px-3 sm:px-4 rounded-[5px] hover:bg-search-bg/80 flex-shrink-0 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        aria-label={isUnfollowing ? "Unfollowing..." : "Unfollow store"}
      >
        {isUnfollowing ? (
          <span className="flex items-center gap-2">
            <Loader2 className="size-3 animate-spin" />
            <span className="hidden sm:inline">Unfollowing...</span>
          </span>
        ) : (
          "Following"
        )}
      </Button>
    </div>
  );
}
